import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {Subject} from 'rxjs';
import {tap} from 'rxjs/operators';

import {HeaderFilter} from 'asax-common';
import {DropdownItemScienceKeyword, ScienceKeywordsService} from '../science-keywords.service';
import {DropdownItem, TextSearchInput} from "../../search-input.model";

@Component({
  selector: 'asax-header-search-input-science-keywords',
  templateUrl: './search-input-science-keywords.component.html',
  styleUrls: ['./search-input-science-keywords.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchInputScienceKeywordsComponent implements OnInit, OnChanges {
  @Input() searchInput: TextSearchInput;

  @Output() outFilter = new EventEmitter<HeaderFilter>();

  scienceKeywordsFilterValue: string;
  dropdownItemScienceKeywords: DropdownItemScienceKeyword[];

  private valueToDisplay: string;
  private valueSubject = new Subject<string>();

  constructor(private readonly scienceKeywordsServiceService: ScienceKeywordsService) {
  }

  ngOnInit(): void {
    this.scienceKeywordsServiceService.fetchScienceKeywords().pipe(
      tap(dropdownItemScienceKeywords => this.dropdownItemScienceKeywords = dropdownItemScienceKeywords),
      tap(() => this.selectDropdownItemsFromValue()),
      tap(() => this.valueChanged())
    ).subscribe();

    this.valueSubject.asObservable().pipe(
      tap(() => this.buildValue()),
      tap(() => this.buildValueToDisplay()),
      tap(() => this.emitFilter())
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dropdownItemScienceKeywords?.length > 0) {
      this.selectDropdownItemsFromValue();
      this.valueChanged();
    }
  }

  valueChanged(): void {
    this.valueSubject.next(undefined);
  }

  onScienceKeywordClick(scienceKeyword: DropdownItem) {
    scienceKeyword.selected = !scienceKeyword.selected;
    this.valueChanged();
  }

  clearSearchInputValue(): void {
    this.dropdownItemScienceKeywords?.map(({scienceKeywords}) => scienceKeywords)
      .flat()
      .forEach((dropdownItem: DropdownItem) => dropdownItem.selected = false);

    this.valueChanged();
  }

  clearScienceKeywordsFilterValue(): void {
    this.scienceKeywordsFilterValue = '';
  }

  shouldScienceCategoryBeDisplayed(dropdownItemScienceKeyword: DropdownItemScienceKeyword): boolean {
    return dropdownItemScienceKeyword.scienceKeywords.find(scienceKeyword => this.shouldScienceKeywordBeDisplayed(scienceKeyword)) !== undefined;
  }

  shouldScienceKeywordBeDisplayed(scienceKeyword: DropdownItem): boolean {
    return this.scienceKeywordsFilterValue === undefined || scienceKeyword?.text.toLowerCase().includes(this.scienceKeywordsFilterValue?.toLowerCase());
  }

  private buildValue(): void {
    this.searchInput.value = this.dropdownItemScienceKeywords?.map(({scienceKeywords}) => scienceKeywords)
      .flat()
      .filter(({selected}) => selected === true)
      .map(({value}) => value)
      .join(',');
  }

  private buildValueToDisplay(): void {
    const numberOfScienceKeywordsSelected = this.dropdownItemScienceKeywords?.map(({scienceKeywords}) => scienceKeywords)
      .flat()
      .filter(({selected}) => selected === true)
      .length;

    if (numberOfScienceKeywordsSelected > 0) {
      this.valueToDisplay = `${numberOfScienceKeywordsSelected} selected`;
    } else {
      this.valueToDisplay = '';
    }
  }

  private selectDropdownItemsFromValue(): void {
    const dropdownItemsSelected: string[] = this.searchInput?.value?.split(',')?.map(token => token?.trim());
    this.dropdownItemScienceKeywords?.map(dropdownItemScienceKeywords => dropdownItemScienceKeywords.scienceKeywords)
      .flat()
      .forEach((dropdownItem: DropdownItem) => {
        if (dropdownItemsSelected?.includes(dropdownItem.value)) {
          dropdownItem.selected = true;
        } else {
          dropdownItem.selected = false;
        }
      });
  }

  private emitFilter(): void {
    const filter = {
      id: this.searchInput.id,
      value: this.searchInput.value,
      valueParsed: this.searchInput.value,
      valueToDisplay: this.valueToDisplay,
      label: this.searchInput.label,
      isDisabled: this.searchInput.isDisabled,
      elasticParserType: this.searchInput.elasticParserType
    };

    this.outFilter.emit(filter);
  }

  onScienceCategoryClick(dropdownItemScienceKeyword: DropdownItemScienceKeyword): void {
    // Toggle the selection for the entire category
    const allSelected = dropdownItemScienceKeyword.scienceKeywords.every(keyword => keyword.selected);
  
    // Select or deselect all science keywords within the category
    dropdownItemScienceKeyword.scienceKeywords.forEach(scienceKeyword => {
      scienceKeyword.selected = !allSelected;
    });
  
    // Trigger value changes after selecting the category
    this.valueChanged();
  }
}